import { useCallback } from 'react'
import * as React from 'react'
import type { DevPanelData } from '../types'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import RadioInput from 'LuxKit/Radio/RadioInput'
import { usePseudoloc, type PseudolocMode } from 'i18n/PseudolocContext'
import { useIntl } from 'react-intl'

interface Props {
  state: DevPanelData
}

const RADIO_GROUP_NAME = 'pseudoloc-mode'

function I18nTab(_props: Props) {
  const intl = useIntl()
  const { mode, setMode } = usePseudoloc()

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMode(e.target.value as PseudolocMode)
  }, [setMode])
  const fullPseudoDescription = intl.formatMessage(
    {
      id: 'temp_dev_panel_full_pseudo_description',
      defaultMessage: '<bold>Full Pseudo</bold> adds brackets and replaces characters with accented versions.',
      description: 'Description of full pseudolocalization mode in the developer tools internationalization tab',
    },
    {
      bold: (chunks: React.ReactNode) => <b key="full-pseudo">{chunks}</b>,
    },
  )
  const lengthPseudoDescription = intl.formatMessage(
    {
      id: 'temp_dev_panel_length_pseudo_description',
      defaultMessage: '<bold>Length Pseudo</bold> adds block characters to simulate translation expansion while keeping text readable.',
      description: 'Description of length-based pseudolocalization mode in the developer tools internationalization tab',
    },
    {
      bold: (chunks: React.ReactNode) => <b key="length-pseudo">{chunks}</b>,
    },
  )

  return <Pane padding={20}>
    <VerticalSpacer gap={16}>
      <Heading variant="heading5">{intl.formatMessage({
        id: 'temp_dev_panel_i18n_heading',
        defaultMessage: 'Internationalization',
        description: 'Heading for the internationalization tab in the customer portal developer tools panel',
      })}
      </Heading>

      <VerticalSpacer gap={12}>
        <BodyText variant="medium" weight="bold">{intl.formatMessage({
          id: 'temp_dev_panel_pseudolocalization_mode_label',
          defaultMessage: 'Pseudolocalization Mode',
          description: 'Section label for pseudolocalization controls in the developer tools internationalization tab',
        })}
        </BodyText>
        <VerticalSpacer gap={8}>
          <RadioInput
            name={RADIO_GROUP_NAME}
            value="off"
            checked={mode === 'off'}
            onChange={handleChange}
          >
            {intl.formatMessage({
              id: 'temp_dev_panel_pseudolocalization_off',
              defaultMessage: 'Off',
              description: 'Radio option label to disable pseudolocalization in the developer tools internationalization tab',
            })}
          </RadioInput>
          <RadioInput
            name={RADIO_GROUP_NAME}
            value="full"
            checked={mode === 'full'}
            onChange={handleChange}
          >
            {intl.formatMessage({
              id: 'temp_dev_panel_pseudolocalization_full',
              defaultMessage: 'Full Pseudo (accented + expanded)',
              description: 'Radio option label for full pseudolocalization mode in the developer tools internationalization tab',
            })}
          </RadioInput>
          <RadioInput
            name={RADIO_GROUP_NAME}
            value="length"
            checked={mode === 'length'}
            onChange={handleChange}
          >
            {intl.formatMessage({
              id: 'temp_dev_panel_pseudolocalization_length',
              defaultMessage: 'Length Pseudo (block padding)',
              description: 'Radio option label for length-based pseudolocalization mode in the developer tools internationalization tab',
            })}
          </RadioInput>
        </VerticalSpacer>
        <BodyText variant="small" colour="neutral-two">
          {intl.formatMessage(
            {
              id: 'temp_dev_panel_pseudolocalization_shortcut',
              defaultMessage: 'Toggle with <bold>Cmd+Alt+Shift+P</bold> to cycle: Off → Full → Length',
              description: 'Keyboard shortcut help text for toggling pseudolocalization modes in the developer tools internationalization tab',
            },
            {
              bold: (chunks: React.ReactNode) => <b key="pseudoloc-shortcut">{chunks}</b>,
            },
          )}
        </BodyText>
      </VerticalSpacer>

      <MessageBanner kind="informational-primary">
        <BodyText variant="small">{fullPseudoDescription}</BodyText>
        <BodyText variant="small">{lengthPseudoDescription}</BodyText>
        <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
          <li>{intl.formatMessage({
            id: 'temp_dev_panel_untranslated_strings_hint',
            defaultMessage: 'Untranslated strings, which won’t have brackets or blocks',
            description: 'Checklist item explaining how pseudolocalization reveals untranslated strings in the developer tools internationalization tab',
          })}
          </li>
          <li>{intl.formatMessage({
            id: 'temp_dev_panel_layout_issues_hint',
            defaultMessage: 'UI layout issues with longer text',
            description: 'Checklist item explaining that pseudolocalization helps reveal layout issues caused by longer translated text',
          })}
          </li>
          <li>{intl.formatMessage({
            id: 'temp_dev_panel_hardcoded_strings_hint',
            defaultMessage: 'Hardcoded strings that bypass i18n',
            description: 'Checklist item explaining that pseudolocalization helps reveal hardcoded strings in the developer tools internationalization tab',
          })}
          </li>
        </ul>
      </MessageBanner>
    </VerticalSpacer>
  </Pane>
}

export default I18nTab
